import React, { useContext } from 'react';
import styled from 'styled-components';

import Button from '../../misc/Button';
import InputSettingsField from './InputSettingsField';
import { PageLogicCondition } from './CollectionConditionalLogicSettingsForm';

import ThemeIcons from '../../../theme/Icons';
import ThemeContext from '../../../theme/ThemeContext';

import ConditionalLogic from '../../../constants/ConditionalLogic';

const StyledWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledStepLabel = styled.div`
  font-weight: 500;
  margin-left: 6px;
  font-size: 14px;
  color: ${(props) => props.theme.fontColorPrimary};
`;

const StyledSectionLabel = styled.div`
  font-weight: 600;
  font-size: 12px;
  margin-bottom: 6px;
  color: ${(props) => props.theme.fontColorSecondary};
`;

const StyledConditionColumn = styled.div`
  display: flex;
  flex-direction: column;
  ${(props) => (props.conditions && props.conditions.length > 1
    ? 'max-height: 450px; overflow-y: auto;'
    : '')};
`;

const StyledConditionRow = styled.div`
  display: flex;
  flex: 1;
  margin-bottom: 8px;
  flex-direction: row;
  align-items: center;
`;

/**
 * Collection-aware Required Logic form. Mirrors CollectionConditionalLogicSettingsForm
 * (parent-column and page/field conditions) but the action governs required state
 * (enforce/unenforce) instead of visibility (show/hide).
 */
export const CollectionRequiredLogicSettingsForm = ({
  targetLabel,
  activeSchemaTable,
  schema,
  logic,
  onChange,
  sortedPages,
  fields,
  fileId,
}) => {
  const theme = useContext(ThemeContext);
  // const { parentId } = activeSchemaTable; // only used by the disabled column-condition handler

  const formattedSortedPages = (sortedPages || [])
    .map((page, i) => ({ ...page, label: `Page ${i + 1}` }));

  const fieldLookup = {};
  (fields || []).forEach((f) => fieldLookup[f._id] = f);

  const handleChange = (key, value) => {
    const nextLogic = JSON.parse(JSON.stringify(logic));
    nextLogic[key] = value;
    if (onChange) onChange(nextLogic);
  };

  /*
   * Parent-table column conditions are disabled for nested-table Required Logic (parity with the
   * root table / all other Required Logic surfaces, which are page/field-only). To re-enable,
   * restore the ColumnLogicCondition import, `parentId`, this handler, the `columnConditions`
   * derivation, and the Column section in the render below.
   *
   * const handleColumnConditionCreate = () => {
   *   const nextLogic = JSON.parse(JSON.stringify(logic));
   *   if (nextLogic.conditions) {
   *     nextLogic.conditions.push({ schema: parentId });
   *   } else {
   *     nextLogic.conditions = [{ schema: parentId }];
   *   }
   *   onChange(nextLogic);
   * };
   */

  const handlePageConditionCreate = () => {
    const nextLogic = JSON.parse(JSON.stringify(logic));
    if (nextLogic.conditions) {
      nextLogic.conditions.push({ file: fileId });
    } else {
      nextLogic.conditions = [{ file: fileId }];
    }
    onChange(nextLogic);
  };

  const handleConditionChange = (index, changes) => {
    const nextLogic = JSON.parse(JSON.stringify(logic));
    if (!nextLogic.conditions[index]) return;
    nextLogic.conditions[index] = {
      ...nextLogic.conditions[index],
      ...changes
    };
    onChange(nextLogic);
  };

  const handleConditionDelete = (index) => {
    const nextLogic = JSON.parse(JSON.stringify(logic));
    nextLogic.conditions.splice(index, 1);
    onChange(nextLogic);
  };

  const indexedConditions = (logic?.conditions || []).map((c, i) => ({ ...c, _originalIndex: i }));
  // const columnConditions = indexedConditions.filter(c => c.schema !== undefined); // Column section disabled
  const pageConditions = indexedConditions.filter(c => c.file !== undefined);

  return (
    <StyledWrapper>
      <StyledConditionRow>
        <div style={{ width: '75px' }}>
          <InputSettingsField
            type="dropdown"
            value={logic.action}
            options={[
              { _id: ConditionalLogic.requiredActions.enforce, value: 'Enforce' },
              { _id: ConditionalLogic.requiredActions.unenforce, value: 'Unenforce' }
            ]}
            onChange={(i, value) => handleChange('action', value)}
          />
        </div>
        <StyledStepLabel theme={theme}>
          this
          {' '}
          {targetLabel}
          {' '}
          when
        </StyledStepLabel>
      </StyledConditionRow>

      <StyledConditionRow>
        <div style={{ width: '75px' }}>
          <InputSettingsField
            type="dropdown"
            inputStyles={{ maxWidth: '100px' }}
            value={logic.eval}
            options={[
              { _id: 'and', value: 'All' },
              { _id: 'or', value: 'Any' }
            ]}
            onChange={(i, value) => handleChange('eval', value)}
          />
        </div>
        <StyledStepLabel theme={theme}>of the following conditions match</StyledStepLabel>
      </StyledConditionRow>

      {/*
        Parent-table column conditions are disabled for nested-table Required Logic (parity with
        the root table / all other Required Logic surfaces, which are page/field-only). The
        Conditional/Visibility logic form keeps its own Column section. To re-enable, restore the
        import, `parentId`, `handleColumnConditionCreate`, `columnConditions`, and this block.

        <StyledSectionLabel theme={theme}>Column</StyledSectionLabel>
        <StyledConditionRow style={{ marginBottom: 0 }}>
          <StyledConditionColumn style={{ flex: 1 }} conditions={columnConditions}>
            {columnConditions.map((condition) => (
              <ColumnLogicCondition
                key={condition._originalIndex}
                index={condition._originalIndex}
                column={condition.column}
                condition={condition.condition}
                value={condition.value}
                schema={schema}
                activeSchemaTable={activeSchemaTable}
                onChange={handleConditionChange}
                onDelete={handleConditionDelete}
              />
            ))}
          </StyledConditionColumn>
        </StyledConditionRow>

        <StyledConditionRow>
          <Button icon={ThemeIcons.misc.add} onClick={handleColumnConditionCreate} />
        </StyledConditionRow>
      */}

      <StyledSectionLabel theme={theme}>Page</StyledSectionLabel>
      <StyledConditionRow style={{ marginBottom: 0 }}>
        <StyledConditionColumn style={{ flex: 1 }} conditions={pageConditions}>
          {pageConditions.map((condition) => (
            <PageLogicCondition
              key={condition._originalIndex}
              index={condition._originalIndex}
              file={condition.file}
              page={condition.page}
              field={condition.field}
              condition={condition.condition}
              value={condition.value}
              fieldLookup={fieldLookup}
              pages={formattedSortedPages}
              onChange={handleConditionChange}
              onDelete={handleConditionDelete}
            />
          ))}
        </StyledConditionColumn>
      </StyledConditionRow>

      <StyledConditionRow>
        <Button icon={ThemeIcons.misc.add} onClick={handlePageConditionCreate} />
      </StyledConditionRow>
    </StyledWrapper>
  );
};

export default CollectionRequiredLogicSettingsForm;
